import _ from 'lodash';
import qs from 'qs';
import type { ChartSettingsStore } from '@/components/Filters/ChartSettings/Store/store';
import type { ObjectTypes } from '@/components/Filters/ObjectType/Store/utils';
import type { Period as PeriodStore } from '@/components/Filters/Period/store/store';
import type { CompareSlug, PeriodSlug } from '@/components/Filters/Period/store/utils';
import type { NumberOfRooms } from '@/components/Filters/Room/Store/utils';
import type { Tenure } from '@/components/Filters/Tenure/store/store';
import type { StatisticsSeries } from '@/types/series';
import type { StatisticsData } from '@/types/statisticsApi';
import { fetchStatistics } from '../fetchStatistics';
import type { Compare, QueryProps, Sort, Period as StatisticsPeriod, StatisticsResource } from '../types';
import { setChartPeriod, setCompare, setTablePeriod } from '../utils/setters';

type FilterToInclude = keyof StatisticsQuery<StatisticsSeries, Compare> | 'areaMode' | 'period';

class StatisticsQuery<Series extends StatisticsSeries, C extends Compare> implements QueryProps<Series, C> {
  resource: StatisticsResource;
  series: Series[];
  ids?: number[];
  developerIds?: number[];
  agencyIds?: number[];
  areaIds?: number[];
  objectTypes?: ObjectTypes[] | null;
  sizes?: string[];
  size?: string | null;
  rooms: NumberOfRooms[] | null;
  projectPhases?: string[];
  minConstructionYear?: string | null;
  maxConstructionYear?: string | null;
  minNumberOfUnits?: string;
  maxNumberOfUnits?: string;
  tenure?: Tenure | null;
  sort?: Sort<Series>;
  period: StatisticsPeriod;
  isNewConstruction?: 1 | 0 | null;
  compare?: C;
  limit?: number;
  fetchMethod?: 'GET' | 'POST';
  periodSlug: PeriodSlug;
  compareSlug: CompareSlug;
  result: StatisticsData<Series, C> | undefined;

  constructor(
    props: QueryProps<Series, C> & {
      customPeriod?: PeriodStore;
      chartSettings?: ChartSettingsStore['chartSettings'];
    },
  ) {
    this.resource = 'areas';
    this.series = [];
    this.ids = [];
    this.developerIds = [];
    this.agencyIds = [];
    this.areaIds = [];
    this.objectTypes = [];
    this.sizes = [];
    this.size = undefined;
    this.rooms = [];
    this.minConstructionYear = '';
    this.maxConstructionYear = '';
    this.minNumberOfUnits = '';
    this.maxNumberOfUnits = '';
    this.tenure = undefined;
    this.projectPhases = [];
    this.sort = {
      period: 'aggregate',
      series: 'name',
      direction: 'asc',
    };
    this.period = {
      start: '',
      end: '',
      window: undefined,
      granularity: undefined,
    };
    this.isNewConstruction = undefined;
    this.compare = undefined;
    this.limit = 10000;

    this.periodSlug = props.customPeriod?.periodSlug ?? 'last4weeks';
    this.compareSlug = props.customPeriod?.compareSlug ?? 'undefined';

    _.merge(this, props);

    if (props.customPeriod) {
      this.period = setTablePeriod(this, props.customPeriod);
    }
    if (props.chartSettings) {
      this.period = setChartPeriod(props.chartSettings);
    }

    if (props.customPeriod?.compareSlug) {
      this.compare = setCompare(this, props.customPeriod.compareSlug);
    }
  }

  getUrlParams() {
    let params: Record<string, string | null | number | undefined> = {
      series: this.series.sort().join(','),
      limit: this.limit,
      'response-format': 'row',
      'start-date': this.period?.start,
      'end-date': this.period?.end,
      // The Statistics API defaults to monthly granularity when no granularity is specified.
      // For custom date ranges, we need granularity to be null for clarity in the application.
      // Setting granularity to 'null' string bypasses the API's default behavior.
      granularity: this.period?.granularity ?? 'null',
      window: this.period?.window,
      compare: this.compare,
      'area-id': this.areaIds?.sort().join(','),
      id: this.ids?.sort().join(','),
      'agency-id': this.agencyIds?.sort().join(','),
      'developer-id': this.developerIds?.sort().join(','),
      rooms: this.rooms?.sort().join(','),
      'basic-object-type': this.objectTypes?.sort().join(','),
      'is-new-construction': this.isNewConstruction,
      tenure: this.tenure,
      //TODO: Make this nicer size: this.sizes?.join(','),
      size: !this.size?.includes('-') ? this.size : undefined,
      'min-size': this.size?.includes('-') ? this.size.split('-')[0] : undefined,
      'max-size': this.size?.includes('-') ? this.size.split('-')[1] : undefined,
      'min-construction-year': this.minConstructionYear,
      'max-construction-year': this.maxConstructionYear,
      'min-number-of-units': this.minNumberOfUnits,
      'max-number-of-units': this.maxNumberOfUnits,
      'project-phase': this.projectPhases?.sort().join(','),
      'sort-series': this.sort?.series,
      'sort-direction': this.sort?.direction,
      'sort-period': this.sort?.period,
    };

    // Removed empty or undefined values
    params = Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== '' && v !== undefined && v !== null));

    return qs.stringify(params);
  }

  url() {
    return `/pro-api/statistics/v2/${this.resource}?${this.getUrlParams()}`;
  }

  async fetchHandler() {
    return await fetchStatistics<Series, C>(this.resource, this.getUrlParams(), this.fetchMethod);
  }

  async fetchBase() {
    const data = await this.fetchHandler();
    this.result = data;

    return data;
  }
}

export { type FilterToInclude, StatisticsQuery };
